import { useEffect } from "react";

/* Recording visualiser — "mirrored spectrum", to the owner's Claude Design spec.
   Pill bars grow from a centre line outward, tallest in the middle, with slow-
   falling peak-hold caps.

   TWO DELIBERATE DEPARTURES FROM THE SUPPLIED MODULE:

   1. It does not call getUserMedia. The spec ships a self-contained class that
      opens its own stream; this app already owns one (zustand-store/global-recorder),
      and a second capture would double-open the microphone, show a second
      permission prompt on some browsers, and drift out of sync with the recorder
      that is actually feeding the transcript. Only the rendering is ported; the
      analyser is handed in.
   2. Colours come from the token layer, not the spec's inline hex. The values are
      identical today — the point is that they follow the palette if it moves.

   Kept from the previous implementation because standards §10 requires them and
   the spec does not mention them: pause off-screen via IntersectionObserver, and
   render one settled frame under prefers-reduced-motion.

   The analyser is shared, so fftSize is raised here and restored on cleanup. It
   is safe: global-recorder's analyser has exactly one consumer, this hook.
   `mic-test` and `use-custom-audio-recorder` each build their own. */

const BANDS = 48;
const FFT_SIZE = 1024;
const SMOOTHING = 0.72;

// Silence must never read as a flat line — the clinician has to be able to tell
// a quiet room from a dead microphone.
const FLOOR = 0.12;
const HEADROOM = 0.82;
const CAP_MAX_DETACH = 0.12;

const token = (name, fallback) =>
  getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;

export const useWaveform = ({ canvasRef, analyser, isRecording, bars = 34, caps = true }) => {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const bands = new Float32Array(BANDS);
    const peaks = new Float32Array(BANDS);

    let frame = null;
    let onScreen = true;
    let last = performance.now();
    let fft = null;
    let previousFftSize = null;

    const accent = token("--ns-accent", "#216351");
    const accentLight = token("--ns-accent-light", "#74C0AB");

    if (analyser) {
      previousFftSize = analyser.fftSize;
      analyser.fftSize = FFT_SIZE;
      analyser.smoothingTimeConstant = SMOOTHING;
      fft = new Uint8Array(analyser.frequencyBinCount);
    }

    const sample = (dt) => {
      analyser.getByteFrequencyData(fft);
      const n = fft.length;

      for (let i = 0; i < BANDS; i += 1) {
        // Perceptual (log-ish) band spacing over the lower ~62% of the spectrum —
        // speech lives there, and the top of the range is mostly empty.
        const lo = Math.floor(Math.pow(i / BANDS, 2.1) * n * 0.62);
        const hi = Math.max(lo + 1, Math.floor(Math.pow((i + 1) / BANDS, 2.1) * n * 0.62));
        let sum = 0;
        for (let j = lo; j < hi; j += 1) sum += fft[j];
        const target = Math.min(1, (sum / (hi - lo) / 255) * 1.9);

        // ~90ms smoothing: fluid, never jittery.
        bands[i] += (target - bands[i]) * Math.min(1, dt * 11);

        // Peak hold: snap up, ease down.
        peaks[i] =
          bands[i] > peaks[i]
            ? bands[i]
            : Math.max(
                bands[i],
                peaks[i] - (peaks[i] - bands[i]) * Math.min(1, dt * 3.2) - dt * 0.12
              );
      }
    };

    /* Size the backing store to the device pixel ratio every frame, so the canvas
       stays crisp and re-measures on resize with no resize listener. */
    const fit = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, w, h);
      return { w, h };
    };

    // roundRect is not universally available; a square bar is a better outcome
    // than a thrown TypeError inside the render loop.
    const pill = (x, y, w, h, r) => {
      if (typeof ctx.roundRect === "function") ctx.roundRect(x, y, w, h, r);
      else ctx.rect(x, y, w, h);
    };

    const draw = () => {
      const { w, h } = fit();
      if (!w || !h) return;

      const n = bars;
      const gap = h > 100 ? 6 : 4;
      const bw = Math.min(h > 100 ? 7 : 4, (w - gap * (n - 1)) / n);
      if (bw <= 0) return;

      const x0 = (w - (n * bw + (n - 1) * gap)) / 2;
      const mid = h / 2;
      const maxH = h * HEADROOM;

      for (let i = 0; i < n; i += 1) {
        const dist = Math.abs(i - (n - 1) / 2) / ((n - 1) / 2); // 0 centre → 1 edge
        const bandIndex = Math.floor(dist * (BANDS - 1) * 0.9);
        const v = Math.min(1, Math.max(0, bands[bandIndex]));
        const shape = Math.pow(1 - dist, 0.5);
        const bh = Math.max(bw, (FLOOR + v * (1 - FLOOR)) * maxH * (0.5 + 0.5 * shape));
        const x = x0 + i * (bw + gap);

        const gradient = ctx.createLinearGradient(0, mid - bh / 2, 0, mid + bh / 2);
        gradient.addColorStop(0, accentLight);
        gradient.addColorStop(0.5, accent);
        gradient.addColorStop(1, accentLight);
        ctx.fillStyle = gradient;
        ctx.globalAlpha = 0.35 + 0.65 * shape;
        ctx.beginPath();
        pill(x, mid - bh / 2, bw, bh, bw / 2);
        ctx.fill();

        if (caps) {
          const peak = Math.min(1, Math.max(v, peaks[bandIndex]));
          const ph = Math.min(
            bh + maxH * CAP_MAX_DETACH,
            (FLOOR + peak * (1 - FLOOR)) * maxH * (0.5 + 0.5 * shape)
          );
          ctx.globalAlpha = 0.28;
          ctx.fillStyle = accent;
          ctx.beginPath();
          pill(x, mid - ph / 2 - 2, bw, 2, 1);
          pill(x, mid + ph / 2, bw, 2, 1);
          ctx.fill();
        }

        ctx.globalAlpha = 1;
      }
    };

    const loop = (now) => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (analyser && fft) sample(dt);
      draw();
      frame = requestAnimationFrame(loop);
    };

    const stop = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = null;
    };

    const run = () => {
      stop();
      if (!isRecording || reduced || !onScreen || !analyser) {
        // The settled state: bars held at the idle floor, no animation.
        bands.fill(0);
        peaks.fill(0);
        draw();
        return;
      }
      last = performance.now();
      frame = requestAnimationFrame(loop);
    };

    run();

    // Pause entirely while scrolled out of view — no rAF loop off-screen (§10).
    const io = new IntersectionObserver(
      ([entry]) => {
        onScreen = entry.isIntersecting;
        run();
      },
      { threshold: 0 }
    );
    io.observe(canvas);

    const ro = new ResizeObserver(() => {
      if (!frame) draw();
    });
    ro.observe(canvas);

    return () => {
      stop();
      io.disconnect();
      ro.disconnect();
      if (analyser && previousFftSize !== null) analyser.fftSize = previousFftSize;
    };
  }, [canvasRef, analyser, isRecording, bars, caps]);
};

export default useWaveform;
